/**
 * The hierarchy of components is as follows:
 * NotificationsOverviewWrapper - queries for artifact collection and triggers data
 * NotificationsOverview - UI that the user interacts with in order to connect/disconnect
 * slack, add/delete notifications
 */
import {
  opArtifactId,
  opArtifactName,
  opArtifactProject,
  opDict,
  opEntityName,
  opProjectEntity,
  opProjectName,
} from '@wandb/weave-ui/cg';
import {useValue} from '@wandb/weave-ui/cgreact';
import * as Panel2 from '@wandb/weave-ui/components/Panel2/panel';
import * as React from 'react';

import {
  EventTriggeringConditionType,
  FetchProjectTriggersQueryResult,
  useEntityQuery,
  useFetchProjectTriggersQuery,
} from '../../../../generated/graphql';
import {NotificationTriggerRow} from '../../../ArtifactCollectionSettings/ArtifactCollectionNotifications';
import {useEntityIntegrations} from '../../../ArtifactCollectionSettings/hooks';
import {SectionPlaceholder} from '../../../ArtifactCollectionSettings/SectionPlaceholder';
import {
  getDescription,
  getNotificationTriggers,
} from '../../../ArtifactCollectionSettings/utils';
import SlackIntegration from '../../../SlackIntegration';
import {UserSettingsPlaceholder} from '../../../UserSettings/UserSettingsPage';
import * as S from './style';

const inputType = 'artifact' as const;
export type ConfigType = {};

export const NotificationsOverviewWrapper: React.FC<
  Panel2.PanelProps<typeof inputType, ConfigType>
> = props => {
  const artifactNode = props.input;
  const overviewValue = useValue(
    React.useMemo(() => {
      const projectNode = opArtifactProject({
        artifact: artifactNode,
      });
      return opDict({
        collectionName: opArtifactName({
          artifact: artifactNode,
        }),
        collectionID: opArtifactId({
          artifact: artifactNode,
        }),
        entityName: opEntityName({
          entity: opProjectEntity({project: projectNode}),
        }),
        projectName: opProjectName({
          project: projectNode,
        }),
      } as any);
    }, [artifactNode])
  );

  const triggersQuery = useFetchProjectTriggersQuery({
    variables: {
      projectName: overviewValue?.result?.projectName,
      entityName: overviewValue?.result?.entityName,
    },
    skip: overviewValue.loading || !overviewValue.result,
  });
  if (
    triggersQuery.loading ||
    triggersQuery.data == null ||
    overviewValue.loading ||
    !overviewValue.result
  ) {
    return <SectionPlaceholder />;
  }
  return (
    <NotificationOverview
      entityName={overviewValue.result.entityName}
      artifactCollectionName={overviewValue.result.collectionName}
      artifactCollectionID={overviewValue.result.collectionID}
      triggersQueryResult={triggersQuery}
    />
  );
};

type NotificationsOverviewProps = {
  entityName: string;
  artifactCollectionName: string;
  artifactCollectionID: string;
  triggersQueryResult: FetchProjectTriggersQueryResult;
};

const NotificationOverview = ({
  entityName,
  artifactCollectionName,
  artifactCollectionID,
  triggersQueryResult,
}: NotificationsOverviewProps) => {
  const entityIntegrations = useEntityIntegrations(entityName);
  const entityQuery = useEntityQuery({
    variables: {
      name: entityIntegrations.entityName,
    },
    skip: entityIntegrations.loading || !entityIntegrations.data,
    fetchPolicy: 'cache-and-network',
  });

  const slackIntegration = React.useMemo(() => {
    for (const i of entityIntegrations.data) {
      if (i.type === 'slack') {
        return i;
      }
    }
    return null;
  }, [entityIntegrations]);

  // Fetch all notification triggers for this artifact collection
  const notificationTriggers = getNotificationTriggers(
    artifactCollectionID,
    triggersQueryResult
  );

  if (entityIntegrations.loading) {
    return <SectionPlaceholder />;
  }
  if (entityQuery.loading || !entityQuery.data) {
    return <UserSettingsPlaceholder />;
  }

  const supportedEvents = [EventTriggeringConditionType.LinkModel];
  type triggerEnabled = {
    enabled: boolean;
    id?: string;
  };
  const enabledNotifications: {
    [T in EventTriggeringConditionType]: triggerEnabled;
  } = {
    [EventTriggeringConditionType.LinkModel]: {enabled: false},
    [EventTriggeringConditionType.CreateArtifact]: {enabled: false},
    [EventTriggeringConditionType.UpdateArtifactAlias]: {enabled: false},
    [EventTriggeringConditionType.AddArtifactAlias]: {enabled: false},
  };

  for (const n of notificationTriggers) {
    const current = n.triggeringCondition.eventType;
    enabledNotifications[current] = {
      enabled: true,
      id: n.id,
    };
  }

  return (
    <>
      {entityIntegrations.data.length === 0 ? (
        <S.SettingsOverviewText>
          Notify the team when changes happen in the model registry.
        </S.SettingsOverviewText>
      ) : (
        supportedEvents.map((e, _) => {
          return (
            <S.SettingsOverview key={`styled-${e}`}>
              <NotificationTriggerRow
                key={e}
                eventType={e}
                eventDescription={getDescription(artifactCollectionName, e)}
                artifactCollectionID={artifactCollectionID}
                enabled={enabledNotifications[e].enabled}
                triggerID={enabledNotifications[e].id}
                slackIntegration={slackIntegration}
                entityRefetch={entityIntegrations.refetch}
                triggersQueryResult={triggersQueryResult}
                artifactCollectionName={artifactCollectionName}
                isRow={false}
              />
            </S.SettingsOverview>
          );
        })
      )}
      <S.IntegrationOverview>
        <SlackIntegration
          entity={entityQuery.data.entity}
          entityRefetch={entityQuery.refetch}
          hideDisconnect={true}
        />
      </S.IntegrationOverview>
    </>
  );
};
